import { Button, Container, IconButton, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import SwiperCore, { Navigation, Pagination, Virtual } from "swiper/core";
import { Swiper, SwiperSlide } from "swiper/react";
import { KvixIcon } from "../../icons";
import { ClickableTitle } from "../../Other";
import { Text } from "../../Primitives";
SwiperCore.use([Navigation, Virtual, Pagination]);
const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: (props) => (props.gutterBottom ? theme.spacing(8) : 0),
        marginTop: (props) => (props.gutterTop ? theme.spacing(8) : 0),
        [theme.breakpoints.down("xl")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("lg")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("md")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("sm")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(3)
        },
        [theme.breakpoints.down("xs")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(2),
            marginBottom: theme.spacing(4),
            overflow: "hidden"
        }
    },
    swiperWrapper: {
        position: "relative"
    },
    swiper: {
        "&.swiper-container": {
            position: "static",
            [theme.breakpoints.down("xs")]: {
                overflow: "visible"
            }
        },
        "& .swiper-button-next, & .swiper-button-prev": {
            display: "none"
        },
        "& .swiper-pagination": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "&.swiper-pagination-bullets": {
                [theme.breakpoints.down("xs")]: {
                    bottom: 55
                },
                bottom: 64
            }
        },
        "& .swiper-pagination-bullet": {
            background: theme.palette.common.white,
            opacity: 1,
            width: 6,
            height: 6
        },
        "& .swiper-pagination-bullet-active": {
            width: 8,
            height: 8,
            background: theme.palette.primary.main
        }
    },
    slide: {
        "&.swiper-slide": {
            width: (props) => (props.slideFullWidth ? "100%" : "auto"),
            marginRight: (props) => (props.noMargin ? 0 : props.isStartpage ? 25 : 18),
            [theme.breakpoints.down("xs")]: {
                marginRight: "12px"
            }
        }
    },
    lastSlide: {
        "&.swiper-slide:last-child": {
            marginRight: 0
        }
    },
    titleRow: {
        display: "flex",
        alignItems: "baseline",
        width: "100%",
        justifyContent: "space-between",
        marginBottom: (props) => (props.description ? theme.spacing(1) : theme.spacing(1)),
        [theme.breakpoints.down("xl")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("lg")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("md")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("sm")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("xs")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        }
    },
    title: {
        fontWeight: 700,
        fontSize: 28,
        [theme.breakpoints.down("sm")]: {
            fontSize: 22
        },
        "&.condensed": {
            fontSize: 16,
            fontWeight: 600
        },
        "&:first-letter": {
            textTransform: "capitalize"
        },
        "&:hover": {
            color: theme.palette.primary.main
        }
    },
    titleWrapper: {
        display: "flex",
        justifyContent: "space-between"
    },
    description: {
        fontWeight: 500,
        marginBottom: theme.spacing(4),
        fontSize: 16,
        [theme.breakpoints.down("sm")]: {
            fontSize: 14
        }
    },
    navigationButton: {
        height: "40px",
        width: "40px"
    },
    seeMore: {
        textTransform: "none",
        display: "flex",
        paddingRight: 0
    },
    channelButtons: {
        display: "flex",
        justifyContent: "flex-end",
        "& > *:nth-child(1)": {
            marginRight: 10
        },
        "& button": {
            borderRadius: 30,
            backgroundColor: "#80808024",
            "&:not(.Mui-disabled)": {
                "& svg": {
                    fill: theme.palette.primary.main
                }
            },
            "&.Mui-disabled": {
                backgroundColor: "#80808024"
            },
            "&:hover": {
                backgroundColor: "#80808024"
            }
        },
        "& .back": {
            transform: "rotate(-90deg)"
        },
        "& .forward": {
            transform: "rotate(90deg)"
        }
    }
}));
export const CardList = (props) => {
    const classes = useStyles(props);
    const { t, ready } = useTranslation("general");
    const cards = props.children;
    const [isBeginning, setIsBeginning] = useState(true);
    const [swiper, setSwiper] = useState(null);
    const [isEnd, setIsEnd] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const history = useHistory();
    const options = {
        pagination: props.paginationButtons ? { clickable: false } : false,
        slidesPerView: "auto",
        loop: props.loop,
        navigation: true,
        observer: true,
        resizeObserver: true
    };
    const slideTo = (n) => {
        if (swiper && !swiper.destroyed) {
            swiper.slideTo(n);
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
        }
    };
    const handleSlideChange = () => {
        if (swiper && !swiper.destroyed) {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
        }
    };
    const handleOnSwiper = (swp) => {
        if (props.initialCard) {
            setTimeout(() => {
                swp.slideTo(props.initialCard, 0);
            }, 50);
        }
        setSwiper(swp);
    };
    useEffect(() => {
        if (!swiper) {
            return;
        }
        if ((cards === null || cards === void 0 ? void 0 : cards.length) === 0) {
            setIsEnd(true);
            setIsBeginning(true);
        }
        else {
            setIsEnd(swiper.isEnd);
            setIsBeginning(swiper.isBeginning);
        }
    }, [cards, swiper]);
    if (!ready) {
        return null;
    }
    return (React.createElement(Container, { maxWidth: "xl", className: classes.root },
        React.createElement("div", { className: classes.titleRow },
            props.title && (React.createElement(ClickableTitle, { title: props.title, titleUrl: props.titleUrl, size: props.titleSize })),
            isMobile ? (React.createElement(React.Fragment, null, props.titleUrl && (React.createElement(Button, { variant: "text", color: "primary", onClick: () => history.push(props.titleUrl), className: classes.seeMore },
                t("showAll"),
                React.createElement(KvixIcon.Chevron.Right, null))))) : (React.createElement(React.Fragment, null, props.noArrows ? null : (React.createElement("div", { className: classes.channelButtons },
                React.createElement(IconButton, { disabled: isBeginning, onClick: () => slideTo((swiper === null || swiper === void 0 ? void 0 : swiper.activeIndex) - 1), className: classes.navigationButton },
                    React.createElement(KvixIcon.Expand.ExpandLess, { className: "back" })),
                React.createElement(IconButton, { disabled: isEnd, onClick: () => slideTo((swiper === null || swiper === void 0 ? void 0 : swiper.activeIndex) + 1), className: classes.navigationButton },
                    React.createElement(KvixIcon.Expand.ExpandLess, { className: "forward" }))))))),
        props.description && React.createElement(Text, { className: classes.description }, props.description),
        React.createElement("div", { className: classes.swiperWrapper }, !!(cards === null || cards === void 0 ? void 0 : cards.length) && (React.createElement(Swiper, Object.assign({ onSwiper: handleOnSwiper, onSlideChange: handleSlideChange, onResize: (e) => {
                setIsBeginning(e.isBeginning);
                setIsEnd(e.isEnd);
            } }, options, { className: classes.swiper }), cards.map((card, index) => (React.createElement(SwiperSlide, { className: classNames(classes.slide, props.isStartpage ? classes.lastSlide : ""), key: index }, card))))))));
};
//# sourceMappingURL=CardList.js.map